import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useMemo, type ReactNode } from "react";
import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import { ThemeModeProvider } from "@/context/ThemeModeContext";
import useThemeMode from "@/hooks/useThemeMode";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/routes/ProtectedRoute";
import HomeRoute from "@/routes/HomeRoute";
import AuthLayout from "@/layouts/AuthLayout";
import MainLayout from "@/layouts/MainLayout";
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import VerifyEmailPage from "@/pages/auth/VerifyEmailPage";
import ResendVerificationPage from "@/pages/auth/ResendVerificationPage";
import ChangePasswordPage from "@/pages/auth/ChangePasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import ProfilePage from "@/pages/ProfilePage";
import StrategiesPage from "@/pages/StrategiesPage";
import BrokerAccountsPage from "@/pages/BrokerAccountsPage";
import OptionsAnalystPage from "@/pages/OptionsAnalystPage";
import InvestingIdeaPage from "@/pages/InvestingIdeaPage";
import Rsi2ChartStandalonePage from "@/pages/Rsi2ChartStandalonePage";
import { OrdersPage, PositionsPage } from "@/pages/positions";
import UserManagementPage from "@/pages/admin/UserManagementPage";
import RoleManagementPage from "@/pages/admin/RoleManagementPage";
import OIBackfillPage from "@/pages/admin/OIBackfillPage";
import StrategyManagementPage from "@/pages/admin/StrategyManagementPage";
import AlgoManagementPage from "@/pages/admin/AlgoManagementPage";
import DataOperationsPage from "@/pages/admin/DataOperationsPage";
import AlgoPnlReportPage from "@/pages/admin/AlgoPnlReportPage";
import AnalyticsInsightsPage from "@/pages/admin/AnalyticsInsightsPage";
import PreMarketDiagnosticsPage from "@/pages/admin/PreMarketDiagnosticsPage";
import VmMaintenancePage from "@/pages/admin/VmMaintenancePage";
import AlgoExecutionMonitorPage from "@/pages/admin/AlgoExecutionMonitorPage";
import RsiMomentumPage from "@/pages/admin/RsiMomentumPage";
import DemaWmaGainerPage from "@/pages/admin/DemaWmaGainerPage";
import EnhancedDemaWmaGainerPage from "@/pages/admin/EnhancedDemaWmaGainerPage";
import RsiTradeReviewPage from "@/pages/admin/rsi-trade-review/RsiTradeReviewPage";
import HelpGuidePage from "@/pages/help/HelpGuidePage";
import AnalyticsRouteTracker from "@/components/AnalyticsRouteTracker";
import NotFoundPage from "@/pages/NotFoundPage";
import ForbiddenPage from "@/pages/ForbiddenPage";

// Theme mode (light/dark) is user-toggleable and persisted to
// localStorage -- see ThemeModeContext. The MUI theme is rebuilt
// whenever mode changes; palette.mode drives MUI's automatic
// dark-surface/text-contrast handling so components built on palette
// tokens (this codebase's established convention) adapt automatically.
function AppThemeProvider({ children }: { children: ReactNode }) {
  const { mode } = useThemeMode();
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: "#0F3D63" },
          secondary: { main: "#00B386" },
        },
        shape: { borderRadius: 8 },
      }),
    [mode]
  );
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

/**
 * Redirects a legacy standalone route (e.g. `/market-opportunities`) to its
 * equivalent tab inside `/investing-idea`. A plain `<Navigate to="...">`
 * with a hardcoded target string drops any other query params the old URL
 * had -- e.g. the Portfolio Tracker's "View Analysis" button links to
 * `/market-opportunities?symbol=RELIANCE`, and that `symbol` param was
 * being silently discarded on redirect, so the deep link never actually
 * pre-filled the search box. This merges the incoming query string in
 * (only overriding `tab`) so those params survive the redirect.
 */
function LegacyTabRedirect({ tab }: { tab: string }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set("tab", tab);
  return <Navigate to={`/investing-idea?${params.toString()}`} replace />;
}

export function App() {
  return (
    <ThemeModeProvider>
      <AppThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <AnalyticsRouteTracker />
            <Routes>
            {/* The public landing page now lives inside this app (see
                `pages/landing/LandingPage.tsx`) instead of the standalone
                static site in /marketing (which stays on disk, unused by
                this route, in case it's still linked to elsewhere).
                `HomeRoute` shows it to visitors and redirects anyone
                already signed in straight to their dashboard. */}
            <Route path="/" element={<HomeRoute />} />

            {/* RegisterPage owns its own full-viewport split-screen layout
                (branding panel + form card) -- it does not use AuthLayout's
                centered single-card wrapper like the other auth pages below. */}
            <Route path="/register" element={<RegisterPage />} />

            <Route element={<AuthLayout />}>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/resend-verification" element={<ResendVerificationPage />} />
            </Route>

            <Route element={<ProtectedRoute />}>
              {/* Standalone, full-page "Open in new tab" view for a single
                  RSI(2) Oversold Pullback idea's chart -- deliberately
                  OUTSIDE `MainLayout` (no sidebar/top nav) so it renders as
                  a maximized, uncluttered view in its own tab/window rather
                  than a second copy of the full app shell. Still gated by
                  the same INVESTMENT_IDEAS module as the rest of the
                  feature -- see `Rsi2ChartStandalonePage.tsx`'s docstring. */}
              <Route element={<ProtectedRoute requiredModule="INVESTMENT_IDEAS" />}>
                <Route path="/investing-idea/rsi2-pullback/chart" element={<Rsi2ChartStandalonePage />} />
              </Route>
              <Route element={<MainLayout />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                {/* User Onboarding + Module Access (Pilot): Investing Idea
                    (Market Opportunities, Portfolio Tracker, Stock Data) lives
                    under the INVESTMENT_IDEAS module -- reserved during the
                    pilot, not yet granted to any customer. Same pattern as
                    the ALGO_STRATEGY-gated block below. */}
                <Route element={<ProtectedRoute requiredModule="INVESTMENT_IDEAS" />}>
                  <Route path="/investing-idea" element={<InvestingIdeaPage />} />
                  {/* Market Opportunities, Portfolio Tracker, and Stock Data
                      used to be 3 separate top-nav items; all three are now
                      tabs inside `/investing-idea` -- see
                      `InvestingIdeaPage`'s own docstring. The 3 old paths
                      redirect here (to the equivalent tab) so existing
                      bookmarks/links keep working. */}
                  <Route path="/stocks" element={<LegacyTabRedirect tab="stock-data" />} />
                  <Route path="/market-opportunities" element={<LegacyTabRedirect tab="market-opportunities" />} />
                  <Route path="/portfolio" element={<LegacyTabRedirect tab="portfolio-tracker" />} />
                </Route>
                {/* User Onboarding + Module Access (Pilot, Admin control): every
                    Options Analyst page/tab (Option Indicator, OI Flow
                    Charts, Strike Charts, AI Insights / Adjust Strategy)
                    lives under the OPTION_ANALYST module. Same route-gating
                    pattern as ALGO_STRATEGY/INVESTMENT_IDEAS below --
                    everyone gets this module by default today (see
                    migration 0032 + `UserRegisterRequest.selected_modules`'s
                    default), but an admin can now revoke it per user via
                    User Management -> Module Access, and this route guard
                    is what makes that revocation actually take the user's
                    Option Analyst pages away, not just hide the nav link. */}
                <Route element={<ProtectedRoute requiredModule="OPTION_ANALYST" />}>
                  {/* AI Strategy Recommendation and Adjust Strategy used to be
                      separate routes (`/strategy-adjustment/:symbol`); both are
                      now tabs inside `/options-dashboard` — see
                      `OptionsDashboardPage`'s own docstring. */}
                  <Route path="/options-dashboard" element={<Navigate to="/options-analyst?tab=ai-insights" replace />} />
                  {/* Option Indicator, OI Flow Charts, and Strike Charts used
                      to be 3 separate routes/sidebar leaves; all three are now
                      tabs inside `/options-analyst` — see
                      `OptionsAnalystPage`'s own docstring. The 3 old paths
                      redirect here (to the equivalent tab) so existing
                      bookmarks/links keep working. */}
                  <Route path="/options-analyst" element={<OptionsAnalystPage />} />
                  <Route path="/option-indicator" element={<Navigate to="/options-analyst?tab=indicator" replace />} />
                  <Route path="/option-indicator/charts" element={<Navigate to="/options-analyst?tab=charts" replace />} />
                  <Route path="/option-indicator/strike-charts" element={<Navigate to="/options-analyst?tab=strike-charts" replace />} />
                </Route>
                {/* User Onboarding + Module Access (Pilot): Algo Strategies,
                    Broker Account, Orders, and Positions all live under the
                    ALGO_STRATEGY module (see app/models/module.py's
                    docstring) -- reserved during the pilot, not yet granted
                    to any customer. Route-gated here so a logged-in user
                    without that module sees the same /forbidden page as an
                    unauthorized admin route, instead of a working-looking
                    shell whose API calls silently 403. */}
                <Route element={<ProtectedRoute requiredModule="ALGO_STRATEGY" />}>
                  <Route path="/strategies" element={<StrategiesPage />} />
                  <Route path="/broker-accounts" element={<BrokerAccountsPage />} />
                  <Route path="/orders" element={<OrdersPage />} />
                  <Route path="/positions" element={<PositionsPage />} />
                </Route>
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/change-password" element={<ChangePasswordPage />} />
                {/* Help & Guide — always visible to every authenticated user
                    regardless of module access, since it's documentation, not
                    a gated feature. Deep-linkable via ?section=<id>, used by
                    the contextual HelpGuideLink icon on Broker Account/Algo
                    Strategies/Option Analyst. */}
                <Route path="/help" element={<HelpGuidePage />} />
                <Route path="/forbidden" element={<ForbiddenPage />} />

                <Route element={<ProtectedRoute requiredRoles={["ADMIN"]} />}>
                  <Route path="/admin/users" element={<UserManagementPage />} />
                  <Route path="/admin/roles" element={<RoleManagementPage />} />
                  <Route path="/admin/oi-backfill" element={<OIBackfillPage />} />
                  <Route path="/admin/strategies" element={<StrategyManagementPage />} />
                  {/* Algo Engine (Phase 1) single-algo management view — NOT
                      a second catalogue. Only the row with `strategy_key`
                      set (TEK-MB01 / "Velocity Edge") links here today; see
                      `AlgoManagementPage`'s own docstring. */}
                  <Route path="/admin/algos/:algoId" element={<AlgoManagementPage />} />
                  <Route path="/admin/data-operations" element={<DataOperationsPage />} />
                  <Route path="/admin/analytics" element={<AnalyticsInsightsPage />} />
                  <Route path="/admin/reports/algo-pnl" element={<AlgoPnlReportPage />} />
                  <Route path="/admin/pre-market-diagnostics" element={<PreMarketDiagnosticsPage />} />
                  <Route path="/admin/vm-maintenance" element={<VmMaintenancePage />} />
                  <Route path="/admin/algo-execution-monitor" element={<AlgoExecutionMonitorPage />} />
                  <Route path="/admin/strategies/rsi-momentum" element={<RsiMomentumPage />} />
                  <Route path="/admin/strategies/rsi-momentum/trade-review" element={<RsiTradeReviewPage />} />
                  <Route path="/admin/strategies/dema-wma-gainer" element={<DemaWmaGainerPage />} />
                  <Route path="/admin/strategies/dema-wma-gainer-enhanced" element={<EnhancedDemaWmaGainerPage />} />
                </Route>
              </Route>
            </Route>

            <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </AppThemeProvider>
    </ThemeModeProvider>
  );
}

export default App;
